import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
} from '@amzn/awsui-components-console';
import { SearchState, SEARCH_MODE } from '../SearchPage';
import { generateLabel } from '../hooks/useSavedSearches';
import { searchGateCode } from '../searchFilterUtils';
import { MIN_QUERY_LENGTH } from '../constants';
import type { SaveResult } from 'src/common/savedSearches';
import { saveSearchHelpText } from 'src/common/savedSearches';
import InfoTooltip from 'src/common/ui/InfoTooltip';
import { ShareSearchButton } from 'src/common/urlSharing';
import { searchStateToParams } from '../urlState';
import styles from './SearchPanelFooter.module.scss';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface SearchPanelFooterProps {
  readonly currentSearchState: SearchState;
  /** Whether the current state may be submitted — see `SearchPage`. */
  readonly canSearch: boolean;
  /** Whether the current state yields a shareable link that will auto-run — see `SearchPage`. */
  readonly canShare: boolean;
  /** Whether "Clear all" has anything to reset — see `SearchPage`. */
  readonly canClear: boolean;
  readonly onSearch: () => void;
  readonly onClear: () => void;
  /** Why the search is refused, or `undefined` when it is not. */
  readonly blockedReason?: string;
  /**
   * Id given to the rendered `blockedReason`, so the query input can point its
   * `aria-describedby` at it. Supplied by `SearchPage` because both ends of that
   * association live in different components.
   */
  readonly blockedReasonId: string;
  /**
   * Persists the current search under a name. Supplied by `SearchPage`, which
   * owns the backend-backed saved-searches hook; the footer stays presentational
   * and only drives the modal. Resolves with `success: false` (and a reason) when
   * the save is rejected, so the modal can surface it and stay open.
   */
  readonly onSaveSearch: (
    state: SearchState,
    name: string,
  ) => Promise<SaveResult>;
}

/** Maximum character length for a saved search name */
const MAX_NAME_LENGTH = 50;

/**
 * The search panel's action band: why the search is blocked on the left, and the
 * controls that act on the whole panel on the right — Search, Save this search,
 * Share search, Clear all.
 *
 * All three are panel-scoped rather than row-scoped ("Clear all" resets the
 * dropdowns, the query text AND Exact match), which is what earns them a footer of
 * their own instead of a place beside the input.
 *
 * The save-search `Modal` lives here with the button that opens it, so the button
 * and the state it drives stay in one component.
 */
const SearchPanelFooter: React.FC<SearchPanelFooterProps> = ({
  currentSearchState,
  canSearch,
  canShare,
  canClear,
  onSearch,
  onClear,
  blockedReason,
  blockedReasonId,
  onSaveSearch,
}) => {
  const { t } = useTranslation();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const handleSaveSearchClick = useCallback(() => {
    // Pre-populate with auto-generated label as a suggestion
    setSaveSearchName(generateLabel(t, currentSearchState));
    setShowSaveModal(true);
  }, [t, currentSearchState]);

  const handleSaveConfirm = useCallback(async () => {
    const trimmedName = saveSearchName.trim();
    if (trimmedName.length === 0) return;
    setIsSaving(true);
    setSaveError('');
    const result = await onSaveSearch(currentSearchState, trimmedName);
    setIsSaving(false);
    if (result.success) {
      setShowSaveModal(false);
      setSaveSearchName('');
      return;
    }
    setSaveError(
      result.duplicateLabel
        ? t('search-save-error-duplicate')
        : result.loadFailed
          ? t('search-save-error-load-failed')
          : result.notReady
            ? t('search-save-error-not-ready')
            : t('search-save-error-generic'),
    );
  }, [t, saveSearchName, currentSearchState, onSaveSearch]);

  const handleSaveCancel = useCallback(() => {
    setShowSaveModal(false);
    setSaveSearchName('');
    setSaveError('');
  }, []);

  const canSave =
    currentSearchState.queryText.trim().length > 0 ||
    Object.values(currentSearchState.filters).some((v) => v && v.length > 0);

  const isSaveDisabled = saveSearchName.trim().length === 0;

  // Share is gated on the search being runnable (`canShare` = canAutoRunSearch),
  // so its reason is derived from the SAME predicate — but re-worded for the share
  // action so it never claims a term/filter is missing when the real gap is the
  // translation locale, and so it does not duplicate the footer's blocked message.
  // Branches on the gate CODE, not on the rendered message. The previous `===`
  // comparison against the message would have stopped matching under any
  // non-English locale, silently falling through to the generic wording.
  const shareGate = searchGateCode(
    currentSearchState.filters,
    currentSearchState.queryText,
    currentSearchState.mode === SEARCH_MODE.SOURCE,
  );
  const shareDisabledReason =
    shareGate === 'TRANSLATED_LOCALE_REQUIRED'
      ? t('search-share-disabled-locale')
      : shareGate === 'QUERY_TOO_SHORT'
        ? t('search-share-disabled-min-length', { min: MIN_QUERY_LENGTH })
        : t('search-share-disabled-criteria');

  return (
    <>
      <div className={styles.footer} data-testid="search-panel-footer">
        {/*
          `Box variant="small"` in the inactive text colour — NEUTRAL, not the red
          error treatment the mock shows. An unmet minimum is the pristine state of
          the page on first load, so an error would flag a mistake the user has not
          made yet. It still satisfies the requirement that a blocked search explain
          itself without a hover, and it disappears as soon as a criterion is
          supplied.

          Deliberately NOT Button `disabledReason`. That is a perfectly valid
          Cloudscape pattern — it swaps native `disabled` for `aria-disabled` on
          purpose, so the button stays focusable and its tooltip reachable. It is
          simply the weaker fit here: `disabledReason` describes the BUTTON, whereas
          this message is wired to the query INPUT via `ariaDescribedby` (the id
          below). Since a natively disabled button leaves the tab order, keyboard
          users land on the query input — the field they need to act on — and hear
          the reason there, with no hover or tooltip involved.

          The reason is panel-level rather than field-level: the criterion can be
          satisfied EITHER by the query input OR by any filter dropdown.
        */}
        <div className={styles.message}>
          {blockedReason && (
            /* `Box`'s own `id` prop is deprecated, so the id goes on an inner span. */
            <Box variant="small" color="text-status-inactive">
              <span id={blockedReasonId}>{blockedReason}</span>
            </Box>
          )}
        </div>

        <div className={styles.actions}>
          <Button variant="primary" onClick={onSearch} disabled={!canSearch}>
            {t('search-submit')}
          </Button>
          {/* Save this search — help text shows on hover/focus of the button itself. */}
          <InfoTooltip content={saveSearchHelpText(t)} disabled={!canSave}>
            <Button
              variant="normal"
              onClick={handleSaveSearchClick}
              disabled={!canSave}
              disabledReason={t('search-save-disabled-reason')}
              iconName="star"
            >
              {t('search-save-this-search')}
            </Button>
          </InfoTooltip>
          {/* Share search — disabled reason shows on hover while it is greyed out. */}
          <ShareSearchButton
            shareParams={searchStateToParams(currentSearchState)}
            disabled={!canShare}
            disabledReason={shareDisabledReason}
          />
          {/*
            Clear all sits last and resets the dropdowns, the query text AND Exact
            match. Rendered as a hyperlink via `Button variant="link"` (not an anchor
            or `Link`): it navigates nowhere but must keep `onClick`/`disabled`
            semantics, and only a real `button` conveys the disabled state to AT.
          */}
          <Button variant="link" onClick={onClear} disabled={!canClear}>
            {t('search-clear-all')}
          </Button>
        </div>
      </div>

      <Modal
        visible={showSaveModal}
        onDismiss={handleSaveCancel}
        header={t('save-search-header')}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleSaveCancel}>
                {t('save-search-cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveConfirm}
                disabled={isSaveDisabled || isSaving}
                loading={isSaving}
              >
                {t('save-search-save')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <FormField
          label={t('save-search-name-label')}
          errorText={saveError || undefined}
          constraintText={t('save-search-char-count', {
            count: saveSearchName.length,
            max: MAX_NAME_LENGTH,
          })}
        >
          <Input
            value={saveSearchName}
            onChange={({ detail }) =>
              setSaveSearchName(detail.value.slice(0, MAX_NAME_LENGTH))
            }
            placeholder={t('save-search-placeholder')}
            ariaLabel={t('save-search-name-aria')}
          />
        </FormField>
      </Modal>
    </>
  );
};

export default SearchPanelFooter;
